import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import {
  API_URL,
  CMR_DESCARCARI_LIST,
  TRUCKS_B,
  TRUCKS_L,
  formatDate,
  isExpiredJWT,
  revertName,
} from "../utils";
import { Controller, useForm } from "react-hook-form";
import { Button, Modal, NumberInput, Select, Table } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { FaEdit, FaFilePdf } from "react-icons/fa";
import CustomError from "./CustomError";
import { ChevronRight } from "lucide-react";

function CmrList() {
  const { auth, refresh } = useContext(AuthContext);
  const [cmrs, setCmrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [err, setErr] = useState(null);
  let location = useLocation();

  const path = location.pathname.match(/\/l\/.*/i) ? "l" : "b";
  const isAdmin = auth.user.permissions.includes("Admin");

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      added_by_user: "",
      start: "",
      end: "",
      locul_descarcarii: "",
      results: 50,
    },
  });

  useEffect(() => {
    reset();
  }, [location, reset]);

  useEffect(() => {
    async function getUserCmrs() {
      try {
        const res = await axios.get(API_URL + `/api/${path}/cmr/normal`, {
          headers: { Authorization: `Bearer ${auth.aT}` },
        });
        setCmrs(res.data);
      } catch (err) {
        console.error(err);
        setErr(err);
      }
    }

    async function getAdminCmrs() {
      try {
        const res = await Promise.all([
          axios.get(API_URL + `/api/${path}/cmr/?results=50`, {
            headers: { Authorization: `Bearer ${auth.aT}` },
          }),
          axios.get(API_URL + `/api/user/?normal=${path}`, {
            headers: { Authorization: `Bearer ${auth.aT}` },
          }),
        ]);
        setCmrs(res[0].data);
        setUsers(res[1].data);
      } catch (err) {
        console.error(err);
        setErr(err);
      }
    }

    if (auth.aT && !isExpiredJWT(auth.user.exp)) {
      if (isAdmin) getAdminCmrs();
      else getUserCmrs();
    } else refresh();
  }, [auth, refresh, path, isAdmin]);

  async function onSubmit(data) {
    let q = API_URL + `/api/${path}/cmr/?`;
    q += `pending=${data.pending}`;
    q += data.added_by_user
      ? `&added_by_user=${revertName(data.added_by_user)}`
      : "";
    q += data.truck ? `&truck=${data.truck}` : "";
    q += data.start ? `&start=${data.start.toISOString()}` : "";
    q += data.end ? `&end=${data.end.toISOString()}` : "";
    q += `&results=${data.results}`;
    q += data.locul_descarcarii
      ? `&locul_descarcarii=${data.locul_descarcarii}`
      : "";
    const aT = isExpiredJWT(auth.user.exp) ? await refresh() : auth.aT;
    try {
      const res = await axios.get(q, {
        headers: { Authorization: `Bearer ${aT}` },
      });
      setCmrs(res.data);
    } catch (err) {
      console.error(err);
      setErr(err);
    }
    close();
  }

  return err ? (
    <CustomError error={err} />
  ) : (
    <div>
      {isAdmin && (
        <Modal
          lockScroll={false}
          opened={opened}
          onClose={close}
          title="Filtreaza"
          classNames={{
            title: "!text-2xl !font-bold",
            body: "flex flex-col gap-4",
          }}
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-3"
          >
            <Controller
              name="added_by_user"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Sofer"
                  clearable
                  placeholder="Selecteaza sofer"
                  nothingFoundMessage="Sofer inexistent"
                  searchable
                  data={users.map((user) => user.formattedName)}
                />
              )}
            />
            <Controller
              name="truck"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Camion"
                  clearable
                  placeholder="Selecteaza camion"
                  nothingFoundMessage="Sofer inexistent"
                  searchable
                  data={path === "l" ? TRUCKS_L : TRUCKS_B}
                />
              )}
            />
            <Controller
              name="start"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  label="Inceput"
                  clearable
                  placeholder="Data si ora de inceput"
                />
              )}
            />
            <Controller
              name="end"
              control={control}
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  label="Sfarsit"
                  clearable
                  placeholder="Data si ora de sfarsit"
                />
              )}
            />
            <Controller
              name="locul_descarcarii"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Locul descarcarii"
                  clearable
                  placeholder="Selecteaza loc descarcare..."
                  nothingFoundMessage="Loc descarcare inexistent"
                  searchable
                  data={CMR_DESCARCARI_LIST}
                />
              )}
            />
            <Controller
              name="results"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  label="Rezultate"
                  placeholder="Limita rezultate"
                  min={1}
                />
              )}
            />
          </form>
          <Button type="submit" onClick={handleSubmit(onSubmit)}>
            Trimite
          </Button>
        </Modal>
      )}

      <h1 className="text-2xl font-bold mb-4">Lista CMR</h1>

      {isAdmin && (
        <Button onClick={open} type="button" className="mb-4">
          Filtreaza
        </Button>
      )}

      <MantineTable cmrs={cmrs} />
      <MobileTable cmrs={cmrs} />
    </div>
  );
}

export default CmrList;

function MantineTable({ cmrs }) {
  const rows = cmrs.map((cmr) => (
    <Table.Tr key={cmr.id}>
      <Table.Td>
        <Link to={String(cmr.id)}>
          <FaEdit className="text-xl" />
        </Link>
      </Table.Td>
      <Table.Td>
        <Link to={`${String(cmr.id)}/view`}>
          <FaFilePdf className="text-xl" />
        </Link>
      </Table.Td>
      <Table.Td>{cmr.id}</Table.Td>
      <Table.Td>{formatDate(cmr.date)}</Table.Td>
      <Table.Td>{cmr.added_by_user.formattedName}</Table.Td>
      <Table.Td>{cmr.locul_descarcarii}</Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="hidden sm:block">
      <Table.ScrollContainer minWidth={300}>
        <Table
          withTableBorder
          withColumnBorders
          highlightOnHover
          verticalSpacing={"sm"}
          classNames={{ thead: "!sticky" }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Edit</Table.Th>
              <Table.Th>PDF</Table.Th>
              <Table.Th>ID</Table.Th>
              <Table.Th>Data</Table.Th>
              <Table.Th>Sofer</Table.Th>
              <Table.Th>Descarcare</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  );
}

function MobileTable({ cmrs }) {
  return (
    <div className=" sm:hidden p-4">
      <div className="divide-y divide-black/50">
        {cmrs.map((cmr) => (
          <Link
            key={cmr.id}
            className="flex items-center justify-between py-4 hover:bg-black/10 px-2  transition-all"
            to={`${String(cmr.id)}/view`}
          >
            <div className="flex flex-col">
              <span className="font-medium">CMR - {cmr.id}</span>
              <span className="text-xs">{formatDate(cmr.date)}</span>
              <span className="text-s">{cmr.locul_descarcarii}</span>
            </div>
            <ChevronRight className="h-5 w-5 " />
          </Link>
        ))}
      </div>
    </div>
  );
}
